import styled from 'styled-components';
import {
  CellWithDropdown,
  Column,
  DatasourceTable,
  newTableTheme,
} from '@ardoq/table-legacy';
import { AsyncStatus } from 'integrations/common/types/api';
import { Connection } from '@ardoq/api-types/integrations';
import { DataSourceEmptyTable } from '@ardoq/integrations';
import { type I18nLocale, t } from '@ardoq/i18n';

type ConnectionsTableParams = {
  listStatus: AsyncStatus;
  connections: Connection[];
  isAdmin: boolean;
  onDelete: (connection: Connection) => void;
  onUpsertClick: (connection?: Connection) => void;
  onConnectionClick: (onConnectionClick: Connection) => void;
  extraColumns?: Column<Connection>[];
  connectYourAccountTitle: string;
  connectYourAccountBtn: string;
  i18nLocale: I18nLocale;
};

export function ConnectionsTable({
  connections,
  listStatus,
  isAdmin,
  onDelete,
  onUpsertClick,
  onConnectionClick,
  extraColumns = [],
  connectYourAccountTitle,
  connectYourAccountBtn,
  i18nLocale,
}: ConnectionsTableParams) {
  return (
    <DatasourceTable
      loading={listStatus === 'LOADING'}
      components={newTableTheme}
      dataSource={connections}
      columns={[
        {
          dataIndex: 'name',
          title: t(
            i18nLocale,
            'integrations:connectionsTable.columns.connectionName',
            'Connection Name'
          ),
          headerStyle: { width: '45%' },
          valueRender: (_, connection) => (
            <ConnectionName onClick={() => onConnectionClick(connection)}>
              {connection.name}
            </ConnectionName>
          ),
        },
        ...extraColumns,
        isAdmin
          ? {
              headerStyle: { float: 'right' },
              valueRender: (_, row) => {
                const isUneditable = 'isUneditable' in row && row.isUneditable;
                const isUndeletable =
                  'isUndeletable' in row && row.isUndeletable;

                const options: Array<{ label: string; onClick: () => void }> =
                  [];

                if (!isUneditable) {
                  options.push({
                    label: t(
                      i18nLocale,
                      'integrations:connectionsTable.actions.editConnection',
                      'Edit Connection'
                    ),
                    onClick: () => onUpsertClick(row),
                  });
                }

                if (!isUndeletable) {
                  options.push({
                    label: t(
                      i18nLocale,
                      'integrations:connectionsTable.actions.delete',
                      'Delete'
                    ),
                    onClick: () => onDelete(row),
                  });
                }

                return options.length > 0 ? (
                  <CellWithDropdown options={options} />
                ) : null;
              },
              cellStyle: { float: 'right', height: '40px', display: 'flex' },
            }
          : {},
      ]}
      renderEmptyTable={() =>
        listStatus !== 'LOADING' ? (
          <DataSourceEmptyTable
            title={t(
              i18nLocale,
              'integrations:connectionsTable.empty.title',
              "You don't have any connections yet"
            )}
            message={connectYourAccountTitle}
            button={{
              text: connectYourAccountBtn,
              onClick: () => onUpsertClick(),
            }}
          ></DataSourceEmptyTable>
        ) : null
      }
    />
  );
}

const ConnectionName = styled.div`
  &:hover {
    text-decoration-line: underline;
    cursor: pointer;
  }
`;
